import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useToast } from '@/hooks/use-toast'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { Mail, Phone } from 'lucide-react'

const SignUp = () => {
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [company, setCompany] = useState('')
  const [role, setRole] = useState('broker')
  const [isLoading, setIsLoading] = useState(false)
  const [signUpMethod, setSignUpMethod] = useState<'email' | 'phone'>('email')
  const [otpSent, setOtpSent] = useState(false)
  const [otp, setOtp] = useState('')
  
  const { signUp, signUpWithPhone, verifyOtp } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  
  usePageTitle("Sign Up")

  // No redirect for buyers — they register here normally, then get sent
  // to the intake questionnaire after email verification via AuthCallback.

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    const { error } = await signUp(email, password, fullName, company, role)

    if (error) {
      if (error.message.includes('Email rate limit exceeded') || error.message.includes('rate limit')) {
        toast({
          title: 'Too Many Requests',
          description: 'Please wait a few minutes before trying again.',
          variant: 'destructive',
        })
      } else if (error.message.includes('already registered') || error.message.includes('already exists')) {
        toast({
          title: 'Email Already Registered',
          description: 'This email is already registered. Please sign in instead.',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Registration Error',
          description: error.message || 'An error occurred during registration.',
          variant: 'destructive',
        })
      }
    } else {
      toast({
        title: 'Success',
        description: 'Account created! Please check your email to verify your account.',
      })
    }

    setIsLoading(false)
  }

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    // Format phone number to include country code if not present
    const formattedPhone = phone.startsWith('+') ? phone : `+61${phone.replace(/^0/, '')}`

    const { error } = await signUpWithPhone(formattedPhone, password, fullName, company, role)

    if (error) {
      toast({
        title: 'Registration Error',
        description: error.message || 'An error occurred during registration.',
        variant: 'destructive',
      })
    } else {
      setOtpSent(true)
      toast({
        title: 'Verification Code Sent',
        description: 'Please enter the 6-digit code sent to your phone.',
      })
    }

    setIsLoading(false)
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    const formattedPhone = phone.startsWith('+') ? phone : `+61${phone.replace(/^0/, '')}`
    const { error } = await verifyOtp(formattedPhone, otp)

    if (error) {
      toast({
        title: 'Verification Failed',
        description: error.message || 'Invalid code. Please try again.',
        variant: 'destructive',
      })
    } else {
      toast({
        title: 'Success',
        description: 'Phone verified! Redirecting to dashboard...',
      })
      navigate('/dashboard')
    }

    setIsLoading(false)
  }

  const RoleSelection = () => (
    <div className="space-y-3">
      <Label>I want to register as:</Label>
      <RadioGroup value={role} onValueChange={setRole}>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="seller" id="seller" />
          <Label htmlFor="seller" className="cursor-pointer">
            <div>
              <div className="font-medium">Business Seller</div>
              <div className="text-sm text-muted-foreground">Sell your business with access to analytics and listings</div>
            </div>
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="broker" id="broker" />
          <Label htmlFor="broker" className="cursor-pointer">
            <div>
              <div className="font-medium">Business Broker</div>
              <div className="text-sm text-muted-foreground">Full broker access with advanced tools and client management</div>
            </div>
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="buyer" id="buyer" />
          <Label htmlFor="buyer" className="cursor-pointer">
            <div>
              <div className="font-medium">Business Buyer</div>
              <div className="text-sm text-muted-foreground">Browse businesses with smart matching and favorites</div>
            </div>
          </Label>
        </div>
      </RadioGroup>
    </div>
  )

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto">
          <Card className="shadow-medium">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-primary">Create Your Account</CardTitle>
              <CardDescription>
                Join Australia's premier business marketplace - available on web and mobile app
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={signUpMethod} onValueChange={(v) => setSignUpMethod(v as 'email' | 'phone')}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </TabsTrigger>
                  <TabsTrigger value="phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="email">
                  <form onSubmit={handleEmailSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="fullName">Full Name</Label>
                      <Input
                        id="fullName"
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company Name</Label>
                      <Input
                        id="company"
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        required
                      />
                    </div>
                    <RoleSelection />
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? 'Creating Account...' : 'Create Account'}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="phone">
                  {!otpSent ? (
                    <form onSubmit={handlePhoneSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="fullNamePhone">Full Name</Label>
                        <Input
                          id="fullNamePhone"
                          type="text"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="companyPhone">Company Name</Label>
                        <Input
                          id="companyPhone"
                          type="text"
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                          required
                        />
                      </div>
                      <RoleSelection />
                      <div className="space-y-2">
                        <Label htmlFor="phone">Mobile Phone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="0412 345 678"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          required
                        />
                        <p className="text-xs text-muted-foreground">Australian mobile number (we'll add +61)</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="passwordPhone">Password</Label>
                        <Input
                          id="passwordPhone"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          minLength={6}
                        />
                      </div>
                      <Button type="submit" className="w-full" disabled={isLoading}>
                        {isLoading ? 'Sending Code...' : 'Send Verification Code'}
                      </Button>
                    </form>
                  ) : (
                    <form onSubmit={handleVerifyOtp} className="space-y-4">
                      <div className="text-center mb-4">
                        <p className="text-sm text-muted-foreground">
                          Enter the 6-digit code sent to {phone}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="otp">Verification Code</Label>
                        <Input
                          id="otp"
                          type="text"
                          placeholder="123456"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          maxLength={6}
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full" disabled={isLoading}>
                        {isLoading ? 'Verifying...' : 'Verify & Create Account'}
                      </Button>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        className="w-full"
                        onClick={() => setOtpSent(false)}
                      >
                        Change Phone Number
                      </Button>
                    </form>
                  )}
                </TabsContent>
              </Tabs>

              <div className="mt-4 text-center text-sm">
                Already have an account?{' '}
                <Link to="/sign-in" className="text-primary hover:underline">
                  Sign in
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default SignUp